import React, {Dispatch, SetStateAction, SyntheticEvent} from 'react';

import {SigninProvider} from 'auth/AuthConstants';
import {Capability} from 'auth/capabilities/CapabilityFeatureConstants';
import {Feature} from 'auth/feature-enablement/FeatureConstants';
import {DataPolicy} from 'auth/PolicyConstants';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {WorkspaceItem} from 'constants/FileBrowserConstants';
import {Genome} from 'constants/GenomeConstants';
import {User} from 'constants/UserConstants';
import {EnzymeList} from 'molbio/enzymes/EnzymeConstants';
import {Model as FeatureLibraryModel} from 'molbio/feature-libraries/FeatureLibrary';
import {ShareLinkMetadata} from 'nav/share/ShareLinkConstants';
import {NotificationOptOutPreferences} from 'notifications/preferences/NotificationPreferences';
import {Organization} from 'orgs/OrganizationConstants';
import {ADMIN_INITIAL_STATE} from 'useradmin/reduceAdminModels';
import {NumberParsingFormat} from 'util/numbers/NumberParsingFormat';

interface BootstrapUserTraits {
  [propName: string]: string | number | boolean;
}

export interface BootstrapUser extends User {
  entityId: string;
  publicIdentifyTraits: BootstrapUserTraits;
  licenses: {[name: string]: {granted: boolean}};
}

interface FullStory {
  identify: (uid: string) => void;
  setUserVars: (userVars: object) => void;
  getCurrentSessionURL?: (val: boolean) => string;
  log: ((msg: string) => void) | ((level: string, msg: string) => void);
  shutdown: () => void;
  restart: () => void;
  consent: (consent: boolean) => void;
}

export interface FullStoryData {
  tags: {
    error_last_minute?: boolean;
  };
  extra: {
    FSSessionURL?: string;
  };
  exception?: {
    value?: string;
  };
  request?: {
    url?: string;
  };
  message: string;
}

// NavLinks can either describe a link or a divider.  The divider will just
// have the property divider with a boolean true.
export type NavLink = NavLinkLink | NavLinkDivider;

interface NavLinkLink {
  text: string;
  href: string;
  method?: string;
}

interface NavLinkDivider {
  divider: true;
}

export interface Bootstrap {
  navLinks?: Array<NavLink>;
  adminStoreState: typeof ADMIN_INITIAL_STATE;
  dataPolicies: Array<DataPolicy>;
  // TODO(Steven): Remove once all clients are using enabledCapabilityFeatures
  enabledCapabilityFeatures: Array<Feature>;
  enabledFeatures: Array<Feature>;
  itemId: string;
  itemName: string;
  navbarColor: string;
  nextURL?: string;
  numberParsingFormat: NumberParsingFormat;
  owner_choices: Array<User | Organization>;
  shareLinkEnabledFeatures: Array<Feature>;
  signinProviders: Array<SigninProvider>;
  tenantProvisionableCapabilities: Array<Capability>;
  componentProps?: object;
  genomes?: Array<Genome>;
  scheduledModalKey?: string;
  shareLinkMetadata?: ShareLinkMetadata;
  sharedFolderItemAPIId?: string;
  workspaceItems?: Array<WorkspaceItem>;
  enzyme_lists?: Array<EnzymeList>;
  feature_libraries?: Array<FeatureLibraryModel>;
  current_entity_id?: string;
  useBearerTokenAuthAPI: boolean;
  entity?: EntityStoreUser;
  api_enabled: boolean;
  api_keys: Array<{id: number; key: string; created_at: string; key_hint: string}>;
  pipebio_token_hint: string;
  pipebio_token_expires_at: string;
  notificationDeliveryTimezone: string;
  notificationOptOutPreferences: NotificationOptOutPreferences;
  // Only present on legacy useradmin pages
  organization_id?: string;
}

declare global {
  interface Window {
    _IS_INTERNAL_ADMIN_PAGE?: boolean;
    _IS_LEGAL_PAGE?: boolean;
    _STATIC_JS_BUILD_PATH?: string;
    analytics?: SegmentAnalytics.AnalyticsJS;
    beforeUnloadType?: string;
    // Boolean used to communicate that then JS bundle finished successfully. If there's a syntax error (e.g.
    // due to code not being properly transpiled), this will be falsy after the script, so we show an error.
    BENCHLING_INITIALIZED?: boolean;
    bootstrap?: Bootstrap;
    bootstrapUser?: BootstrapUser;
    BUILD_TIMESTAMP?: Date;
    shareAccessIdentifier?: string;
    chrome?: object;
    clientAPIVersion: string;
    debug?: boolean;
    DATADOG_LOGS_CLIENT_TOKEN?: string;
    DATADOG_RUM_APPLICATION_ID?: string;
    DATADOG_RUM_CLIENT_TOKEN?: string;
    DEPLOY_ENVIRONMENT: string;
    DEPLOY_NAME: string;
    DEVTOOLS_ENABLE_APOLLO_DEVTOOLS: boolean;
    ENABLE_EDITOR_PENDING_REQUEST_CHECK: boolean;
    ENABLE_SYNCER_METRICS: boolean;
    FS: FullStory;
    impersonating?: boolean;
    initializeGainsightPx?(): void;
    aptrinsic?: (...args: Array<unknown>) => unknown;
    isBrowserSupported?: boolean;
    isGoogleBot?: boolean; // A cookie set by TopSpot through GTM.
    // @eric (2019-07-24):
    // window.onerror's type definition was fixed from `message: Event | string` to `message: string`
    // via https://github.com/microsoft/TypeScript/issues/2838 , but a regression was introduced when
    // they regenerated the definitions in https://github.com/microsoft/TypeScript/pull/22381
    onerror: {
      (message: string, source?: string, fileno?: number, columnNumber?: number, error?: Error): void;
    };
    REVISION: string;
    SENTRY_DSN_JS?: string;
    SENTRY_DSN_JS_NSB?: string;
    SUPPRESS_USERAGENT_ERROR?: boolean;
    TENANT_NAME: string;
    TENANT_SUBDOMAIN: string;
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    trafficSrcCookie: any;
    // Deprecated, don't use in new code!
    user: BootstrapUser;
    userprefs: {[key: string]: unknown};
    bnchAptrinsicCallbacks?: Array<() => void>;
  }

  // `var` is the only way to extend the global type declaration as of node 16.
  // See: https://stackoverflow.com/a/68328575
  /* oxlint-disable vars-on-top, no-var */
  var __activeDevServiceWorker: ServiceWorker | undefined;
  var __BUILD_TIMESTAMP__: string | undefined;
  var __devServiceWorkerEnabled: boolean | undefined;
  var __NODE__: boolean | undefined;
  var __NODE_TESTING__: boolean | undefined;
  var __r: typeof React | undefined;
  var debug: boolean | undefined;
  var debugSetConfig: (key: string, val: object) => Promise<void> | undefined;
  /* oxlint-enable vars-on-top, no-var */
}

/**
 * Call this function in a default/else case that should never happen due to a type narrowed to
 * never. It converts the value to `any` since we may want to assertAndContinue with a message
 * containing the unexpected runtime value.
 *
 * Motivation here:
 * https://www.typescriptlang.org/docs/handbook/advanced-types.html#exhaustiveness-checking
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export function assertNever(value: never): any {
  return value;
}

export function isFunction(value: unknown): value is Function {
  return typeof value === 'function';
}

export type NonEmptyArray<T> = [T, ...Array<T>];

export function isNonEmptyArrayOfType<T>(
  obj: unknown,
  predicate: (elem: unknown) => boolean
): obj is NonEmptyArray<T> {
  return Array.isArray(obj) && obj.length > 0 && predicate(obj[0]);
}

// Extract the type from an array, e.g. `ArrayElement<Array<string>>` is string.
export type ArrayElement<T> = T extends Array<infer R> ? R : never;

/**
 * Display type in its evaluated form, useful for union types, etc.
 *
 * @example
 * type PartA = { a: string };
 * type PartB = { b: number };
 * // Displayed as "PartA | PartB"
 * type Composed = PartA | PartB;
 * // Displayed as "{ a: string } | { b: number }"
 * type ReadableComposed = MakeReadable<Composed>;
 */
export type MakeReadable<T> = T extends infer S ? {[K in keyof S]: S[K]} : never;
export type NoneOf<T> = MakeReadable<{[K in keyof T]?: never}>;
export type AtMostOneOf<T> =
  | NoneOf<T>
  | {[K in keyof T]: MakeReadable<Pick<T, K> & NoneOf<Omit<T, K>>>}[keyof T];
export type AtLeastOneOf<T> = {
  [K in keyof T]: MakeReadable<Required<Pick<T, K>> & Partial<Omit<T, K>>>;
}[keyof T];

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type Params<T extends (...args: Array<any>) => any> = T extends (...args: infer R) => any ? R : any;
export type Optional<T> = T | null | undefined;
type MakeAllFieldsNonNullable<T> = {[P in keyof T]: NonNullable<T[P]>};
export type MakeAllFieldsOptional<T> = {[P in keyof T]?: Optional<T[P]>};
export type MakeAllFieldsRequired<T> = Required<MakeAllFieldsNonNullable<T>>;
/**
 * Makes all keys of given type `T` non-optional by ensuring all optional properties are required.
 *
 * Note that this differs from MakeAllFieldsRequired in that it does not remove null as an allowed type.
 *
 * @template T - The type to modify.
 * @example
 * type Example = { a?: string; b?: number };
 * type Result = MakeAllFieldsNonOptional<Example>;
 * // Result is { a: string; b: number }
 */
export type MakeAllFieldsNonOptional<T> = {[K in keyof T]-?: T[K]};
export type MakeFieldsOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type MakeFieldsRequired<T, K extends keyof T> = T & Required<MakeAllFieldsNonNullable<Pick<T, K>>>;
export type MakeFieldsNonOptional<T, K extends keyof T> = T & MakeAllFieldsNonOptional<Pick<T, K>>;

/**
 * Helpers for React component types
 */
export type WithDefaultPropsOptional<Props, DefaultProps> = Partial<Props> &
  // https://stackoverflow.com/a/51717096
  Pick<Props, Exclude<keyof Props, keyof DefaultProps>>;

// Simple event structure for when we manually build an event-like object.
export interface GenericEvent {
  target: {
    value: string;
  };
}
export type BasicEvent = SyntheticEvent<HTMLElement>;
export type UIEvent = React.UIEvent<HTMLElement>;
export type SelectChangeEvent = React.ChangeEvent<HTMLSelectElement>;
export type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
export type InputFocusEvent = React.FocusEvent<HTMLInputElement>;
export type TextAreaChangeEvent = React.ChangeEvent<HTMLTextAreaElement>;

export interface NumericInputState {
  isValid: boolean;
  numericValue: number | null;
  value: string;
  errorMessage?: string | null;
}

// Change event type for NumericInput
export interface NumericInputChangeEvent {
  target: NumericInputState;
}
// Type for NumericInputs that want to preserve the exact user-entered value
export interface NumericInputString {
  value: string;
  isValid: boolean;
}

export type ElementKeyboardEvent = React.KeyboardEvent<HTMLElement>;
export type ElementMouseEvent = React.MouseEvent<HTMLElement>;
export type ElementWheelEvent = React.WheelEvent<HTMLElement>;
export type ElementClipboardEvent = React.ClipboardEvent<HTMLElement>;
export type ElementDragEvent = React.DragEvent<HTMLElement>;
export type ElementFormEvent = React.FormEvent<HTMLElement>;
export type InputElementFocusEvent = React.FocusEvent<HTMLInputElement>;

// Define some useful unit values that can be used similarly to null for better type-safety or for
// when null already means something. TypeScript lets us define the same name as a type and as a
// constant so that it can be used either as a value or as a type.
// For example, a form input might have type number | UNSPECIFIED.
export type UNSPECIFIED = 'UNSPECIFIED';
export const UNSPECIFIED: UNSPECIFIED = 'UNSPECIFIED';

// Constant to be used to indicate that we do not have access to a resource
// instead of just returning a null value.
export type INACCESSIBLE = 'INACCESSIBLE';
export const INACCESSIBLE: INACCESSIBLE = 'INACCESSIBLE';

// Constant indicating that a value is in the process of loading.
export type LOADING = 'LOADING';
export const LOADING: LOADING = 'LOADING';

// See https://github.com/englercj/jquery-ajax-progress
interface ProgressJQueryAjaxSettings extends JQueryAjaxSettings {
  progress?(e: ProgressEvent): void;
}

export interface ProgressJQuery extends JQueryStatic {
  ajax(settings: ProgressJQueryAjaxSettings): JQueryXHR;
  ajax(url: string, settings?: JQueryAjaxSettings): JQueryXHR;
}

/**
 * Utility type for passing around the setter returned from useState as a callback. Helpful when using
 * the function updater instead of passing in the new value, e.g. `setCount((prevCount: number) => count + 1)`
 * instead of `setName('Naomi')`.
 *
 * Usage:
 *    // First
 *    const [count, setCount] = useState<number>(0);
 *    fancyMath(setCount);
 *    // Then
 *    function fancyMath(setCount: HookSetState<number>) {...do something with setCount}
 */
export type HookSetState<T> = Dispatch<SetStateAction<T>>;

/**
 * Utility type to define a function used as a generic type for a function that takes any function in its
 * generic. The type is also compatible with utility types like Parameters and ReturnType, which don't work
 * with TypeScript's built in Function type that is intended to solve a similar problem as this type.
 */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericFunction = (...args: Array<any>) => any;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export type PromiseFn<TArgs extends Array<any>, TOut> = (...args: TArgs) => Promise<TOut>;

/**
 * `JSONLeafValue` encompasses the JSON leaf types. We use the term "leaf" to distinguish them from:
 *   - Non-leaf, complex types: array and object. See `JSONValue`, `JSONObject`, and `JSONArray`, which
 *     defines those types recursively.
 *   - `JSONScalar`, which is an internal GraphQL type that is equivalent to `JSONValue`.
 *     - Normally, we'd name this type `JSONScalarValue` because externally, "scalar" means all types other
 *       than array and object. However, we've already internally defined `JSONScalar` to include those.
 */
export type JSONLeafValue = string | number | boolean | null;
export interface JSONObject {
  [key: string]: JSONValue;
}
export interface JSONArray extends Array<JSONValue> {}
// Copied from https://github.com/microsoft/TypeScript/issues/3496#issuecomment-128553540
export type JSONValue = JSONLeafValue | JSONObject | JSONArray;

export function isJSONArray(value: JSONValue): value is JSONArray {
  return Array.isArray(value);
}

export function isJSONObject(value: JSONValue): value is JSONObject {
  return typeof value === 'object' && value != null && !isJSONArray(value);
}

export function assertJSONObject(value: JSONValue): asserts value is JSONObject {
  if (!isJSONObject(value)) {
    throw new Error(
      `Expected JSONObject, got ${value == null ? 'null' : isJSONArray(value) ? 'array' : typeof value}`
    );
  }
}

// Copied from https://github.com/microsoft/TypeScript/issues/33308#issuecomment-529308349,
// Useful for narrowing enums
export type PickKey<T, K extends keyof T> = Extract<keyof T, K>;

/**
 * Useful for overriding the types of fields in an interface.
 * @example
 *   interface GQLData {
 *     id: string;
 *     name: string;
 *     personalData: any;
 *   }
 *
 *   interface PersonalData {
 *     contacts: Array<Person>;
 *     addresses: Array<Address>;
 *   }
 *
 *   type Data = Override<GQLData, {personalData: PersonalData}>;
 */
export type Override<OriginalType, OverrideType> = Omit<OriginalType, keyof OverrideType> & OverrideType;

/**
 * Useful for giving auto-complete options for a string while not requiring the string to be in T.
 *
 * @example
 * type Color: LiteralUnion = 'red' | 'green';
 * const x: Color = '';
 * // An autocomplete menu with red and green will show up, but "purple" would also typecheck.
 * @see https://github.com/microsoft/TypeScript/issues/29729#issuecomment-505826972
 */
export type LiteralUnion<T extends U, U = string> = T | (U & {});

/**
 * Useful for marking all fields optional recursively.
 * @see https://typeofnan.dev/creating-your-own-deeppartial-type-in-typescript/
 */
export type DeepPartial<T> = T extends object ? {[P in keyof T]?: DeepPartial<T[P]>} : T;

export interface Constructor {
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  new (...args: any): any;
}

// Define interface of Input element event https://stackoverflow.com/a/43176930
export interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

// Type of a generic constructable Backbone View (i.e. the class itself, not an instance of it). Normally you
// can use `typeof Foo` but that doesn't work for generic classes since Foo is just a type, not a value.
export interface GenericBackboneViewClass<Model extends Backbone.Model, View extends Backbone.View<Model>> {
  new (options?: Backbone.ViewOptions<Model>): View;
}

/**
 * Get all keys of the input type.
 *
 * This works well for unions of object types where not all keys exist on all members of the union, where
 * using `keyof T` would only give the intersection of all keys.
 *
 * @example
 * ```
 * type A = {a: string; b: string} | {a: string}
 * type Keys1 = keyof A; // "a"
 * type Keys2 = AllKeys<A>; // "a" | "b"
 * ```
 */
export type AllKeys<T> = T extends object ? keyof T : never;

export interface RectBounds {
  top: number;
  left: number;
  bottom: number;
  right: number;
}

/**
 * A utility type that resolves and simplifies a given type `T` by
 * mapping each of its properties directly. This is useful for
 * normalizing type behavior, especially when dealing with inferred
 * or computed types.
 *
 * @template T - The type to be resolved.
 * @example
 * type Original = { a: string } & { b: number };
 * type Resolved = Resolve<Original>;
 * // Resolved is { a: string; b: number }
 */
export type Resolve<T> = {
  [K in keyof T]: T[K];
};

/**
 * A utility type that recursively traverses a type `T`, and replaces all instances
 * of a property key `OldKey` with a new key `NewKey`, while also changing the type
 * of that property to `NewType`. This transformation is applied deeply through
 * nested objects and arrays of objects.
 *
 * @template T - The target type to transform.
 * @template OldKey - The property key to search for and replace.
 * @template NewKey - The new property key name to use in place of `OldKey`.
 * @template NewType - The type to assign to all properties renamed to `NewKey`.
 *
 * @example
 * type Original = {
 *   name: string;
 *   children: Array<{
 *     name: string;
 *     friends: Array<{ name: string; age: number }>;
 *   }>;
 * };
 * type Transformed = RenameAndReplaceKey<Original, 'name', 'fullName', Array<string>>;
 * // Transformed is:
 * // {
 * //   fullName: Array<string>;
 * //   children: {
 * //     fullName: Array<string>;
 * //     friends: {
 * //       fullName: Array<string>;
 * //       age: number;
 * //     }[];
 * //   }[];
 * // }
 */
export type RenameAndReplaceKey<T, OldKey extends string, NewKey extends string, NewType> =
  T extends Array<infer U>
    ? Array<RenameAndReplaceKey<U, OldKey, NewKey, NewType>>
    : T extends object
      ? {
          [K in keyof T as K extends OldKey ? NewKey : K]: K extends OldKey
            ? NewType
            : RenameAndReplaceKey<T[K], OldKey, NewKey, NewType>;
        }
      : T;
